import { useState } from 'react';
import {
  AlertTriangle,
  Bot,
  IndianRupee,
  MessageSquare,
  ShieldCheck,
  ShoppingCart,
  TrendingUp,
  UserPlus,
} from 'lucide-react';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorAlert,
  PageHeader,
  Skeleton,
  StatCard,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { useQueueSummary } from '@/lib/customers-api';
import { useInquirySummary } from '@/lib/crm-api';
import { useOrderSummary } from '@/lib/orders-api';
import { useAssistantSummary } from '@/lib/assistant-api';
import {
  CHART_COLORS,
  PERIOD_LABEL,
  REPORT_PERIODS,
  useCategorySales,
  useOrderStatusBreakdown,
  useOverview,
  useSalesTrend,
  useTopCustomers,
  useTopProducts,
  type ReportPeriod,
} from '@/lib/reports-api';
import { cn, formatCompactCurrency, formatCurrency, formatNumber } from '@/lib/utils';

/**
 * The dashboard (spec §41, §59).
 *
 * Every figure is fetched. Until this phase the screen rendered
 * `preview-data.ts` — plausible numbers that looked exactly like real ones,
 * which is why it survived three phases without anyone noticing. Nothing here
 * computes a total or a share; the server owns those, so this screen and the
 * screen a number also appears on cannot drift apart.
 *
 * Charts render an explicit empty state rather than an empty axis. A chart with
 * no data is indistinguishable from a chart that failed to load, and on a
 * revenue dashboard that difference matters.
 */

/** A day label the axis can fit: "22 Aug". */
/** Recharts paints its tooltip white inline; point it at the theme tokens instead. */
const TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-surface)',
  borderColor: 'var(--color-line)',
  color: 'var(--color-ink)',
  borderRadius: 8,
  fontSize: 12,
};

function axisDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export function DashboardPage() {
  const [days, setDays] = useState<ReportPeriod>(30);

  const overview = useOverview(days);
  const trend = useSalesTrend(days);
  const statuses = useOrderStatusBreakdown();
  const categories = useCategorySales(days);
  const topProducts = useTopProducts(days);
  const topCustomers = useTopCustomers(days);

  // The operational counters come from each area's own summary endpoint, so
  // the dashboard agrees with the screen the reader clicks through to.
  const orders = useOrderSummary();
  const queue = useQueueSummary();
  const inquiries = useInquirySummary();
  const assistant = useAssistantSummary();

  const trendPoints = trend.data ?? [];
  const hasRevenue = trendPoints.some((point) => point.revenue > 0);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="How the business is trading, and what is waiting on someone."
        action={
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Reporting period">
            {REPORT_PERIODS.map((period) => (
              <button
                key={period}
                type="button"
                role="tab"
                aria-selected={days === period}
                onClick={() => setDays(period)}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  days === period
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-muted hover:bg-canvas hover:text-ink',
                )}
              >
                {PERIOD_LABEL[period]}
              </button>
            ))}
          </div>
        }
      />

      {/* --- Headline figures ------------------------------------------------ */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {overview.isLoading ? (
          [0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-28" />)
        ) : overview.isError ? (
          <div className="sm:col-span-2 xl:col-span-4">
            <ErrorAlert title="Could not load the figures">
              Reporting needs the <code>report.view</code> permission.
            </ErrorAlert>
          </div>
        ) : (
          <>
            <StatCard
              label="Revenue"
              value={formatCompactCurrency(overview.data?.revenue ?? 0)}
              // Undefined rather than 0 when there is no baseline: the tile then
              // shows no delta at all, instead of claiming a flat period.
              change={overview.data?.revenueChangePercent ?? undefined}
              changeLabel="vs previous"
              icon={<IndianRupee className="size-5" aria-hidden="true" />}
              tone="success"
            />
            <StatCard
              label="Orders"
              value={formatNumber(overview.data?.orders ?? 0)}
              change={overview.data?.ordersChangePercent ?? undefined}
              changeLabel="vs previous"
              icon={<ShoppingCart className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="Average order"
              value={formatCompactCurrency(overview.data?.averageOrderValue ?? 0)}
              icon={<TrendingUp className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="New customers"
              value={formatNumber(overview.data?.newCustomers ?? 0)}
              change={overview.data?.newCustomersChangePercent ?? undefined}
              changeLabel="vs previous"
              icon={<UserPlus className="size-5" aria-hidden="true" />}
              tone="accent"
            />
          </>
        )}
      </div>

      {/* --- What is waiting on someone -------------------------------------- */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <ActionTile
          label="Awaiting verification"
          value={queue.data?.awaitingReview}
          loading={queue.isLoading}
          icon={<ShieldCheck className="size-5" aria-hidden="true" />}
          tone="warning"
        />
        <ActionTile
          label="Orders awaiting payment"
          value={orders.data?.pending}
          loading={orders.isLoading}
          icon={<AlertTriangle className="size-5" aria-hidden="true" />}
          tone="warning"
        />
        <ActionTile
          label="Unworked leads"
          value={inquiries.data?.['unassigned']}
          loading={inquiries.isLoading}
          icon={<MessageSquare className="size-5" aria-hidden="true" />}
          tone="brand"
        />
        <ActionTile
          label="Assistant could not answer"
          value={assistant.data?.unanswered}
          loading={assistant.isLoading}
          icon={<Bot className="size-5" aria-hidden="true" />}
          tone="neutral"
        />
      </div>

      {/* --- Trend and mix ---------------------------------------------------- */}
      <div className="mb-5 grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader title="Revenue" subtitle={PERIOD_LABEL[days]} />
          <div className="h-72 p-4">
            {trend.isLoading ? (
              <Skeleton className="h-full" />
            ) : !hasRevenue ? (
              // An empty axis reads as a broken chart; say which it is.
              <EmptyState
                title="No revenue in this period"
                description="Try a longer window, or check that orders are being placed."
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendPoints}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-line)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={axisDate}
                    tick={{ fontSize: 11, fill: 'var(--color-ink-subtle)' }}
                    // A year of daily points would overlap into a smear.
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis tickFormatter={formatCompactCurrency} tick={{ fontSize: 11, fill: 'var(--color-ink-subtle)' }} width={64} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value) => formatCurrency(Number(value ?? 0))}
                    labelFormatter={(label) =>
                      new Date(String(label)).toLocaleDateString('en-IN', { dateStyle: 'medium' })
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue"
                    stroke={CHART_COLORS[0]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Order pipeline" subtitle="Every order, by status" />
          <div className="h-72 p-4">
            {statuses.isLoading ? (
              <Skeleton className="h-full" />
            ) : (statuses.data?.length ?? 0) === 0 ? (
              <EmptyState title="No orders yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statuses.data}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {(statuses.data ?? []).map((slice, index) => (
                      <Cell
                        key={slice.name}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => formatNumber(Number(value ?? 0))} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* --- Leaderboards ------------------------------------------------------ */}
      <div className="grid gap-4 xl:grid-cols-3">
        <Card>
          <CardHeader title="Top products" subtitle="By revenue" />
          {topProducts.isLoading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-10" />
              ))}
            </div>
          ) : (topProducts.data?.length ?? 0) === 0 ? (
            <EmptyState title="Nothing sold in this period" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th className="text-right">Units</Th>
                  <Th className="text-right">Revenue</Th>
                </tr>
              </thead>
              <tbody>
                {topProducts.data?.map((product) => (
                  <tr key={product.name}>
                    <Td className="max-w-0 truncate text-ink">{product.name}</Td>
                    <Td className="text-right tabular-nums">{formatNumber(product.sold)}</Td>
                    <Td className="text-right font-medium tabular-nums">
                      {formatCompactCurrency(product.revenue)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card>
          <CardHeader title="Top customers" subtitle="By spend" />
          {topCustomers.isLoading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-10" />
              ))}
            </div>
          ) : (topCustomers.data?.length ?? 0) === 0 ? (
            <EmptyState title="No orders in this period" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Customer</Th>
                  <Th className="text-right">Orders</Th>
                  <Th className="text-right">Spent</Th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.data?.map((customer) => (
                  <tr key={customer.id}>
                    <Td className="max-w-0 truncate text-ink">{customer.name}</Td>
                    <Td className="text-right tabular-nums">{customer.orders}</Td>
                    <Td className="text-right font-medium tabular-nums">
                      {formatCompactCurrency(customer.spent)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card>
          <CardHeader title="Category mix" subtitle={PERIOD_LABEL[days]} />
          {categories.isLoading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-10" />
              ))}
            </div>
          ) : (categories.data?.length ?? 0) === 0 ? (
            <EmptyState title="Nothing sold in this period" />
          ) : (
            <ul className="divide-y divide-line">
              {categories.data?.map((slice, index) => (
                <li key={slice.name} className="flex items-center gap-3 px-4 py-3">
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{slice.name}</span>
                  <Badge tone="neutral">{slice.percent}%</Badge>
                  <span className="w-20 text-right text-sm tabular-nums text-ink-muted">
                    {formatCompactCurrency(slice.value)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

/**
 * A queue tile.
 *
 * Renders an em dash rather than a zero while loading or on failure — a count
 * of work waiting is exactly the number nobody should read as "none" when it is
 * really "unknown".
 */
function ActionTile({
  label,
  value,
  loading,
  icon,
  tone,
}: {
  label: string;
  value: number | undefined;
  loading: boolean;
  icon: React.ReactNode;
  tone: 'brand' | 'warning' | 'neutral';
}) {
  if (loading) return <Skeleton className="h-24" />;

  return (
    <StatCard
      label={label}
      value={value === undefined ? '—' : formatNumber(value)}
      icon={icon}
      tone={tone}
    />
  );
}
